import { FC } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { CreateRequestInput, useSignUpMutation } from '@/app/graphql/generated'
import { SelectPhoneComponent } from '@/app/shared/components/select-phone'
import { buildUtmTags } from '@/app/shared/lib/attribution'
import { trackApplicationSend } from '@/app/shared/lib/analytics'
import { ISignUpLessonQuery } from '@/app/shared/components/sign-up-lesson/sign-up-lesson.component'
import useTailwindColor from '@/app/shared/hooks/useTailwindColor'
import { validation } from '@/app/shared/services/validation'
import useLayout, { ITypeSite } from '@/app/shared/store/layout.store'
import useModal from '@/app/shared/store/modals.store'
import { useMarkersStore } from '@/app/shared/store/markers.store'
import { ButtonComponent } from '@/app/shared/ui/button'
import { InputComponent } from '@/app/shared/ui/input'
import { LoadingComponent } from '@/app/shared/ui/loading'
import { SelectComponent } from '@/app/shared/ui/select'
import { customToast } from '@/core/lib/react-toast'

import { ContentComponent } from '../../../modals/content'

//interface
interface ILessonForm {
  data: ISignUpLessonQuery
  typeSite: ITypeSite | null
}

// component
const LessonFormComponent: FC<Readonly<ILessonForm>> = ({ data }) => {
  const affiliates =
    useLayout((state) => state.affiliates)?.map((el) => ({
      value: el?.id ?? '',
      label: el?.metro ?? '',
    })) ?? []
  const { getColor } = useTailwindColor()
  const typeSite = useLayout((state) => state.typeSite)

  const isOnline = typeSite !== 'offline'

  const landing = useLayout((state) => state.landing)

  const methods = useForm<CreateRequestInput>({
    defaultValues: {
      full_name: '',
    },
  })

  const { open } = useModal((store) => store)
  const markers = useMarkersStore((state) => state.markers)

  const [mutate, { loading }] = useSignUpMutation({
    onCompleted: () => {
      open({
        component: () => (
          <ContentComponent
            title='Дякуємо!'
            content='Заявку отримано — наш менеджер незабаром з вами зв’яжеться.'
          />
        ),
      })
      // customToast(data.createRequest?.message ?? 'success', 'success')
      methods.reset()

      trackApplicationSend()
    },
  })

  const handleSubmitForm = async (data: CreateRequestInput) => {
    if (!landing) return

    await mutate({
      variables: {
        input: {
          ...data,
          landing_id: +landing.id,
          affiliates_id: data?.affiliates_id ? +data?.affiliates_id : undefined,
          phone: data?.phone?.replace(/\D/g, ''),
          is_lessons: true,
          utm_tags: JSON.stringify(buildUtmTags(markers)),
        },
      },
    })
  }

  // return
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmitForm)} className={'relative'}>
        <Controller
          name={'full_name'}
          control={methods.control}
          rules={{
            required: validation().required,
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <InputComponent
              onChange={onChange}
              value={value}
              placeholder={"Ваше ім'я"}
              className={'mb-3'}
              isInvalid={Boolean(error)}
              errorMessage={error?.message}
            />
          )}
        />
        <SelectPhoneComponent
          name={'phone'}
          className={'mb-3'}
          isRequired
          colorType={getColor('text-success')}
        />
        {!isOnline && (
          <Controller
            name={'affiliates_id'}
            control={methods.control}
            rules={{ required: validation().required }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <SelectComponent
                placeholder={'Зручний філіал'}
                className={'mb-3'}
                aria-label={'affiliate'}
                option={affiliates}
                isInvalid={Boolean(error)}
                errorMessage={error?.message}
                onChange={onChange}
                value={value?.toString()}
              />
            )}
          />
        )}
        <span className={'mb-[30px] block text-[12px] text-content1-300 laptop:mb-[50px]'}>
          {data.description.description}
        </span>
        <ButtonComponent
          type={'submit'}
          color={'success'}
          size={'md'}
          className={'mx-auto w-fit'}
          isLoading={loading}
        >
          {data.button.text}
        </ButtonComponent>
        <LoadingComponent isShow={loading} />
      </form>
    </FormProvider>
  )
}

export default LessonFormComponent
